import React, { useState, useEffect } from 'react';
import { resolveHasLiked, resolveLikesCount } from '../utils/menuLikes';
import './MenuItemDetail.css';
import DatePicker from 'react-datepicker';
import InstacartService from '../services/instacartService';
import { selectMenuItems, getMatch } from '../services/communityKitchenService';
import cartService from '../services/cartService';
import guestCartService from '../services/guestCartService';
import { toast } from 'react-hot-toast';
import MealPlanModal from './MealPlanModal';
import MenuEditForm from './MenuEditForm';
import { useLocation, useNavigate } from 'react-router-dom';
import { event as gaEvent } from '../utils/analytics';
import SEO from './SEO';
import { buildBreadcrumbs, absoluteUrl } from '../utils/seoConfig';
import { formatIngredientParts } from '../utils/quantityFormat';
import { useWeightUnit } from '../hooks/useWeightUnit';
import { publicTagsFrom } from '../constants/menuTaxonomy';

/**
 * Convert a free-form time string ("15 min", "1 hour 30 minutes") into an
 * ISO 8601 duration ("PT15M", "PT1H30M") so it can be embedded in the
 * Recipe schema for Google rich results.
 */
const toIsoDuration = (timeStr) => {
  if (!timeStr) return undefined;
  const matches = String(timeStr).match(/(\d+)\s*(hour|hr|h|minute|min|m)/gi) || [];
  let hours = 0;
  let minutes = 0;
  matches.forEach((part) => {
    const m = part.match(/(\d+)\s*(hour|hr|h|minute|min|m)/i);
    if (!m) return;
    const num = parseInt(m[1], 10);
    if (m[2].toLowerCase().startsWith('h')) hours += num;
    else minutes += num;
  });
  if (!hours && !minutes) return undefined;
  return `PT${hours ? `${hours}H` : ''}${minutes ? `${minutes}M` : ''}`;
};

/**
 * Clean a stored time value for display. Strips any leading "Prep/Cook/Total
 * Time" label and collapses duplicated/abbreviated units, so already-saved
 * values like "Prep: 10 minutes mins" render as "10 minutes" (the UI adds its
 * own "Prep:" label).
 */
const cleanTimeDisplay = (timeStr) => {
  if (!timeStr) return '';
  return String(timeStr)
    .replace(/\s+/g, ' ')
    .replace(/^(cook|prep|total|active|additional|rest|chill|marinate)\s*(?:time)?\s*[:\-]?\s*/i, '')
    .replace(/^time\s*[:\-]?\s*/i, '')
    .replace(/\bmins?\b/gi, 'minutes')
    .replace(/\bhrs?\b/gi, 'hours')
    .replace(/(minutes?)(\s+minutes?)+/gi, 'minutes')
    .replace(/(hours?)(\s+hours?)+/gi, 'hours')
    .trim();
};

/** Build a Schema.org Recipe JSON-LD object from a MenuItem. */
const buildRecipeJsonLd = (item) => {
  if (!item) return null;
  const slug = item.menuId || item._id;
  const url = absoluteUrl(`/menu/${slug}`);
  const ingredients = (item.ingredients || []).map((i) => {
    const qty = i.quantity ? `${i.quantity} ` : '';
    const unit = i.unit ? `${i.unit} ` : '';
    return `${qty}${unit}${i.name || ''}`.trim();
  });
  const instructions = (item.instructions || []).map((step, idx) => ({
    '@type': 'HowToStep',
    position: idx + 1,
    text: typeof step === 'string' ? step : step?.text || '',
  }));
  const nutrition = item.nutrition
    ? {
        '@type': 'NutritionInformation',
        calories: item.nutrition.Calories,
        proteinContent: item.nutrition.Protein,
        carbohydrateContent: item.nutrition.Carbohydrates,
        fatContent: item.nutrition.Fat,
        fiberContent: item.nutrition.Fiber,
        sodiumContent: item.nutrition.Sodium,
      }
    : undefined;
  const recipe = {
    '@context': 'https://schema.org',
    '@type': 'Recipe',
    name: item.title,
    description: item.description,
    image: item.image ? [item.image] : undefined,
    url,
    recipeCuisine: item.cuisine,
    recipeCategory: publicTagsFrom(item.tags)[0],
    keywords: [item.cuisine, ...(item.dietary || []), ...publicTagsFrom(item.tags)]
      .filter(Boolean)
      .join(', '),
    suitableForDiet: (item.dietary || []).map((d) => `https://schema.org/${d.replace(/[^a-zA-Z]/g, '')}Diet`),
    recipeYield: item.servings ? `${item.servings} servings` : undefined,
    prepTime: toIsoDuration(item.prepTime),
    cookTime: toIsoDuration(item.cookTime),
    totalTime: toIsoDuration(item.totalTime),
    recipeIngredient: ingredients,
    recipeInstructions: instructions,
    nutrition,
    aggregateRating:
      item.likes && item.likes.length >= 5
        ? {
            '@type': 'AggregateRating',
            ratingValue: '5',
            reviewCount: item.likes.length,
          }
        : undefined,
  };
  // Strip undefined values so JSON-LD stays clean.
  Object.keys(recipe).forEach((k) => recipe[k] === undefined && delete recipe[k]);
  return recipe;
};

const MenuItemDetail = ({ item, onClose }) => {
  const weightUnit = useWeightUnit();
  const userString = localStorage.getItem('user');
  const user = userString ? JSON.parse(userString) : null;
  const [showMealPlanModal, setShowMealPlanModal] = useState(false);
  const [hasLiked, setHasLiked] = useState(() => resolveHasLiked(item, user));
  const [likesCount, setLikesCount] = useState(() => resolveLikesCount(item));
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIngredients, setSelectedIngredients] = useState(new Set());
  const [preferredServings, setPreferredServings] = useState('');
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(true);
  const [includeSpices, setIncludeSpices] = useState(false);
  const [includeOils, setIncludeOils] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [addedToList, setAddedToList] = useState(false);
  const [addingToList, setAddingToList] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [addingToCK, setAddingToCK] = useState(false);
  const ckActiveMatch = sessionStorage.getItem('ck_active_match');
  const location = useLocation();
  const navigate = useNavigate();
  
  const isAdmin = user?.isAdmin || false;

  // Get affiliate ID from URL if it exists
  const urlParams = new URLSearchParams(window.location.search);
  const affiliateId = urlParams.get('affiliate');

  const handleClose = () => {
    const returnPath = location.state?.returnPath;
    if (returnPath) {
      navigate(returnPath, { replace: true });
    } else {
      // Fallback to previous behavior
      const urlParams = new URLSearchParams(window.location.search);
      const context = urlParams.get('context');
      const affiliateId = urlParams.get('affiliate');

      let url;
      if (context === 'admin') {
        url = '/admin/dashboard';
      } else if (context === 'affiliate') {
        url = '/affiliate/dashboard';
      } else {
        url = affiliateId ? `/menu?affiliate=${affiliateId}` : '/menu';
      }

      if (onClose) {
        onClose(url);
      } else {
        navigate(url, { replace: true });
      }
    }
  };

  // Close "More" dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showMoreMenu && !e.target.closest('.midl-more-wrapper')) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoreMenu]);

  useEffect(() => {
    setLikesCount(resolveLikesCount(item));
    setHasLiked(resolveHasLiked(item, user));
  }, [item?._id, user?.sub]);

  useEffect(() => {
    if (!item?._id) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const trackUserView = async () => {
      try {
        await fetch(`${process.env.REACT_APP_API_URL}/menu/${item._id}/user-activity`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ incrementViews: 1 })
        });
      } catch (err) {
        console.error('Failed to track user menu view:', err);
      }
    };

    trackUserView();
  }, [item?._id]);

  useEffect(() => {
    const checkFavorite = async () => {
      if (!item?._id || !user) return;
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_API_URL}/menu/${item._id}/favorite`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setIsFavorited(data.isFavorited);
        }
      } catch (err) {
        // Silently fail -- non-critical
      }
    };
    checkFavorite();
  }, [item?._id, user?.sub]);

  useEffect(() => {
    const loadPreferences = async () => {
      if (item && user) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/preferences/${user.sub}/ingredients/${item._id}`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
              },
            }
          );

          if (response.ok) {
            const preferences = await response.json();
            
            // Check if we have actual preferences data with selected ingredients
            if (preferences && Array.isArray(preferences.selectedIngredients) && preferences.selectedIngredients.length > 0) {
              setSelectedIngredients(new Set(preferences.selectedIngredients));
              setPreferredServings(preferences.preferredServings?.toString() || item.servings.toString());
              
              // Set spice and oil toggles directly from preferences if available
              if (preferences.includeSpices !== undefined) {
                setIncludeSpices(preferences.includeSpices);
              } 
              
              if (preferences.includeOils !== undefined) {
                setIncludeOils(preferences.includeOils);
              } 
              
              // If the explicit toggle preferences aren't available, infer from selected ingredients
              if (preferences.includeSpices === undefined || preferences.includeOils === undefined) {
                updateSpiceAndOilToggles(preferences.selectedIngredients);
              }
            } else {
              initializeDefaultIngredients();
            }
          } else {
            initializeDefaultIngredients();
          }
        } catch (error) {
          console.error('Error loading preferences:', error);
          initializeDefaultIngredients();
        }
        setIsLoadingPreferences(false);
      } else if (item) {
        initializeDefaultIngredients();
        setIsLoadingPreferences(false);
      }
    };

    const initializeDefaultIngredients = () => {
      // Create an array of indices for non-spice and non-oil ingredients
      const defaultSelected = [];
      
      item.ingredients.forEach((ingredient, index) => {
        if (typeof ingredient === 'object' && !(ingredient.isSpice || ingredient.isOil)) {
          defaultSelected.push(index);
        }
      });
      
      // Set the state with the default selected ingredients
      setSelectedIngredients(new Set(defaultSelected));
      
      // Set preferred servings to the default from the recipe
      setPreferredServings(item.servings.toString());
      
      // Make sure the spice and oil toggles are off
      setIncludeSpices(false);
      setIncludeOils(false);
    };

    const updateSpiceAndOilToggles = (selectedIngredients) => {
      const spiceIndices = item.ingredients
        .map((ing, index) => ({ ing, index }))
        .filter(({ ing }) => ing.isSpice)
        .map(({ index }) => index);

      const oilIndices = item.ingredients
        .map((ing, index) => ({ ing, index }))
        .filter(({ ing }) => ing.isOil)
        .map(({ index }) => index);

      setIncludeSpices(spiceIndices.every(index => selectedIngredients.includes(index)));
      setIncludeOils(oilIndices.every(index => selectedIngredients.includes(index)));
    };

    loadPreferences();
  }, [item?._id, user?.sub]);

  useEffect(() => {
    if (item && item.ingredients) {
      const totalIngredients = item.ingredients.length;
      const selectedCount = selectedIngredients.size;
      
      // Log which types of ingredients are selected
      const selectedSpices = item.ingredients
        .filter((ing, idx) => ing.isSpice && selectedIngredients.has(idx))
        .length;
      
      const selectedOils = item.ingredients
        .filter((ing, idx) => ing.isOil && selectedIngredients.has(idx))
        .length;
      
      const totalSpices = item.ingredients.filter(ing => ing.isSpice).length;
      const totalOils = item.ingredients.filter(ing => ing.isOil).length;
    }
  }, [selectedIngredients, item]);

  // Function to handle spice toggle
  const handleSpiceToggle = (checked) => {
    setIncludeSpices(checked);
    setSelectedIngredients(prev => {
      const newSet = new Set(prev);
      item.ingredients.forEach((ing, index) => {
        if (ing.isSpice) {
          if (checked) {
            newSet.add(index);
          } else {
            newSet.delete(index);
          }
        }
      });
      
      // Save preferences with the updated toggle state
      setTimeout(() => {
        savePreferences(newSet, undefined, checked, undefined);
      }, 0);
      
      return newSet;
    });
  };

  // Function to handle oil toggle
  const handleOilToggle = (checked) => {
    setIncludeOils(checked);
    setSelectedIngredients(prev => {
      const newSet = new Set(prev);
      item.ingredients.forEach((ing, index) => {
        if (ing.isOil) {
          if (checked) {
            newSet.add(index);
          } else {
            newSet.delete(index);
          }
        }
      });
      
      // Save preferences with the updated toggle state
      setTimeout(() => {
        savePreferences(newSet, undefined, undefined, checked);
      }, 0);
      
      return newSet;
    });
  };

  // Update toggle ingredient function to allow individual selection
  const toggleIngredient = (index) => {
    setSelectedIngredients(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      
      // Save preferences with the current toggle states
      setTimeout(() => savePreferences(newSet, undefined, undefined, undefined), 0);
      
      return newSet;
    });
  };

  // Update select all function
  const handleSelectAll = () => {
    const allSelected = item.ingredients.map((_, index) => index);
    setSelectedIngredients(new Set(allSelected));
    
    // Update spice and oil toggles
    setIncludeSpices(true);
    setIncludeOils(true);
    
    // Save preferences after selecting all, including toggle states
    setTimeout(() => savePreferences(new Set(allSelected), undefined, true, true), 0);
  };

  // Function to scale ingredient quantity based on servings
  const scaleIngredient = (ingredient, scaleFactor) => {
    if (typeof ingredient === 'string') return ingredient;

    return {
      ...ingredient,
      quantity: ingredient.quantity ? (ingredient.quantity * scaleFactor) : ingredient.quantity,
      // The stored display string describes the unscaled amount, so drop it once
      // the recipe is scaled and let the formatter re-derive the fraction.
      quantityDisplay: scaleFactor === 1 ? ingredient.quantityDisplay : undefined
    };
  };

  // Function to validate and update servings
  const handleServingsChange = (event) => {
    const value = event.target.value;
    
    // Allow empty string for temporary input state
    if (value === '') {
      setPreferredServings('');
      return;
    }
    // Allow only positive numbers
    if (/^\d*\.?\d*$/.test(value) && parseFloat(value) > 0) {
      setPreferredServings(value);
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        setSelectedIngredients(selectedIngredients => {
          const newSet = new Set(selectedIngredients);
          const scaledIngredients = item.ingredients
            .filter((_, index) => newSet.has(index))
            .map(ingredient => scaleIngredient(ingredient, numericValue / item.servings));
          
          // Save preferences with the current toggle states
          setTimeout(() => savePreferences(new Set(scaledIngredients.map((ingredient, index) => index)), numericValue, undefined, undefined), 0);
          
          return newSet;
        });
      }
    }
  };

  // Add onBlur handler to ensure empty values are reset
  const handleServingsBlur = () => {
    if (preferredServings === '' || parseFloat(preferredServings) <= 0) {
      const defaultServings = item?.servings?.toString() || '1';
      setPreferredServings(defaultServings);
      setSelectedIngredients(selectedIngredients => {
        const newSet = new Set(selectedIngredients);
        const scaledIngredients = item.ingredients
          .filter((_, index) => newSet.has(index))
          .map(ingredient => scaleIngredient(ingredient, parseFloat(defaultServings) / item.servings));
        
        // Save preferences with the current toggle states
        setTimeout(() => savePreferences(new Set(scaledIngredients.map((ingredient, index) => index)), parseFloat(defaultServings), undefined, undefined), 0);
        
        return newSet;
      });
    }
  };

  // Add these new functions for the stepper buttons
  const incrementServings = () => {
    const newServings = parseFloat(preferredServings) + 0.5;
    setPreferredServings(newServings.toString());
    setTimeout(() => savePreferences(null, newServings, undefined, undefined), 0);
  };

  const decrementServings = () => {
    if (parseFloat(preferredServings) > 0.5) {
      const newServings = parseFloat(preferredServings) - 0.5;
      setPreferredServings(newServings.toString());
      setTimeout(() => savePreferences(null, newServings, undefined, undefined), 0);
    }
  };

  const shouldSelectIngredient = (ingredient, index) => {
    // Simply check if the index is in the selectedIngredients set
    const isSelected = selectedIngredients.has(index);
    return isSelected;
  };

  // Add error checking
  if (!item) {
    console.error('MenuItemDetail received null/undefined item');
    return (
      <div className="menu-detail-overlay" onClick={handleClose}>
        <div className="menu-detail-container error-container">
          <div className="error">
            <h3>Error: Menu Item Not Found</h3>
            <p>The requested menu item could not be loaded.</p>
            <button onClick={handleClose} className="close-button">Close</button>
          </div>
        </div>
      </div>
    );
  }

  // Validate required properties
  const requiredProps = ['_id', 'title', 'description', 'ingredients', 'instructions'];
  const missingProps = requiredProps.filter(prop => !item[prop]);
  
  if (missingProps.length > 0) {
    console.error('MenuItemDetail: Item missing required properties:', missingProps);
    return (
      <div className="menu-detail-overlay" onClick={handleClose}>
        <div className="menu-detail-container error-container">
          <div className="error">
            <h3>Error: Invalid Menu Item</h3>
            <p>The menu item data is incomplete or invalid.</p>
            <p>Missing properties: {missingProps.join(', ')}</p>
            <button onClick={handleClose} className="close-button">Close</button>
          </div>
        </div>
      </div>
    );
  }

  const handlePrint = () => {
    const printContent = document.getElementById('printable-recipe').innerHTML;
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = 'none';
    document.body.appendChild(printFrame);
    const printWindow = printFrame.contentWindow;
    
    printWindow.document.write(`
      <html>
        <head>
          <title>${item.title} Recipe</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              line-height: 1.4; 
              padding: 15px;
              max-width: 100%;
              box-sizing: border-box;
              margin: 0;
            }

            /* Header Layout */
            .menu-detail-header {
              display: flex;
              gap: 20px;
              margin-bottom: 15px;
            }

            .header-content {
              flex: 1;
            }

            h1 {
              margin: 0 0 10px 0;
              font-size: 24px;
              color: #333;
            }

            .detail-image {
              width: 200px;
              height: 200px;
              object-fit: cover;
              border-radius: 4px;
            }

            /* Time Info — wrap instead of overflowing when cook/total
               strings are long (same approach as the on-screen desktop row). */
            .time-info {
              display: flex;
              flex-wrap: wrap;
              align-items: center;
              gap: 8px 20px;
              margin-bottom: 10px;
              font-size: 12px;
              color: #666;
            }

            .time-info > * {
              flex: 0 0 auto;
              white-space: nowrap;
            }

            /* Main Content Layout */
            .menu-detail-content {
              display: grid;
              grid-template-columns: 2fr 1fr;
              gap: 20px;
            }

            /* Left Column */
            .main-content {
              grid-column: 1;
            }

            /* Right Column */
            .side-content {
              grid-column: 2;
              font-size: 11px;
            }

            /* Section Headers */
            h3 {
              font-size: 14px;
              color: #2d3748;
              border-bottom: 1px solid #e2e8f0;
              padding-bottom: 4px;
              margin: 0 0 8px 0;
            }

            /* Description */
            .description-section {
              margin-bottom: 15px;
            }

            .description-section p {
              font-size: 12px;
              margin: 0;
            }

            /* Lists */
            .menu-item-equipment-list,
            .ingredients-list {
              list-style: none;
              padding: 0;
              margin: 0 0 15px 0;
              font-size: 11px;
            }

            .menu-item-equipment-list li,
            .ingredients-list li {
              margin-bottom: 4px;
            }

            /* Instructions */
            .instructions-list {
              padding-left: 20px;
              margin: 0;
              font-size: 11px;
              columns: 2;
              column-gap: 20px;
            }

            .instructions-list li {
              margin-bottom: 6px;
              break-inside: avoid;
            }

            /* Nutrition Table */
            .nutrition-section {
              margin-top: 15px;
            }

            .nutrition-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 10px;
            }

            .nutrition-table tr:last-child {
              border-bottom: none;
            }

            .nutrition-table td {
              padding: 3px 0;
            }

            .nutrition-table td:first-child {
              font-weight: 500;
              width: 60%;
            }

            .nutrition-table td:last-child {
              text-align: right;
              width: 40%;
            }

            /* Hide Interactive Elements */
            .no-print,
            .servings-control,
            .ingredient-checkbox,
            .select-all-btn,
            .buy-ingredients-button,
            .ingredients-tip {
              display: none !important;
            }

            @page {
              margin: 0.5cm;
              size: letter portrait;
            }

            @media print {
              body {
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="menu-detail-header">
            <div class="header-content">
              <h1>${item.title}</h1>
              <div class="time-info">
                <span><i class="far fa-clock"></i> Prep: ${cleanTimeDisplay(item.prepTime)}</span>
                <span><i class="fas fa-fire"></i> Cook: ${cleanTimeDisplay(item.cookTime)}</span>
                <span><i class="fas fa-hourglass-half"></i> Total: ${cleanTimeDisplay(item.totalTime)}</span>
              </div>
            </div>
            <img src="${item.image}" alt="${item.title}" class="detail-image" />
          </div>

          <div class="menu-detail-content">
            <div class="main-content">
              <section class="description-section">
                <h3>Description</h3>
                <p>${item.detailedDescription || item.description}</p>
                ${item.affiliateDetails && item.affiliateDetails.slug ? `
                  <div class="creator-page-link">
                    <a href="/creator/${item.affiliateDetails.slug}">
                      View all recipes by ${item.affiliateDetails.businessName || 'this creator'}
                    </a>
                  </div>
                ` : ''}
                ${item.affiliateDetails && item.affiliateDetails.website ? `
                  <div class="affiliate-website">
                    <p>
                      <i class="fas fa-globe"></i>
                      <a href="${item.affiliateDetails.website}" target="_blank" rel="noopener noreferrer">
                        Visit website
                      </a>
                    </p>
                  </div>
                ` : ''}
              </section>

              <section class="instructions-section">
                <h3>Instructions</h3>
                <ol class="instructions-list">
                  ${item.instructions.map(step => `
                    <li>${step}</li>
                  `).join('')}
                </ol>
              </section>
            </div>

            <div class="side-content">
              <section class="ingredients-section">
                <h3>Ingredients</h3>
                <ul class="ingredients-list">
                  ${item.ingredients.map(ingredient => `
                    <li>
                      ${typeof ingredient === 'object' 
                        ? `${(() => {
                            const parts = formatIngredientParts(ingredient, 1, weightUnit);
                            return `${parts.amount} ${parts.unit} ${ingredient.name}${ingredient.prepNotes ? ` ${ingredient.prepNotes}` : ''}`;
                          })()}`
                        : ingredient}
                    </li>
                  `).join('')}
                </ul>
              </section>

              <section class="equipment-section">
                <h3>Kitchen Equipment</h3>
                <ul class="menu-item-equipment-list">
                  ${item.equipment.map(tool => `
                    <li>${tool}</li>
                  `).join('')}
                </ul>
              </section>

              ${item.nutrition ? `
                <section class="nutrition-section">
                  <h3>Nutrition Facts</h3>
                  <table class="nutrition-table">
                    <tbody>
                      ${Object.entries(item.nutrition).map(([key, value]) => `
                        <tr>
                          <td>${key}</td>
                          <td>${value}</td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>
                </section>
              ` : ''}
            </div>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    setTimeout(() => {
      printFrame.contentWindow.focus();
      printFrame.contentWindow.print();
      setTimeout(() => document.body.removeChild(printFrame), 1000);
    }, 250);
  };

  const handleLike = async (e) => {
    e.stopPropagation();
    if (!user) {
      toast.error('Please sign in to like recipes');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Authentication token missing. Please sign in again.');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/menu/${item._id}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to like menu item');
      const data = await response.json();
      setLikesCount(data.likes);
      setHasLiked(data.hasLiked);
    } catch (error) {
      toast.error('Failed to like recipe. Please try again.');
    }
  };

  const handleToggleFavorite = async () => {
    if (!user) {
      toast.error('Please sign in to save favorites');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/menu/${item._id}/favorite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to update favorite');
      const data = await response.json();
      setIsFavorited(data.isFavorited);
      toast.success(data.isFavorited ? 'Added to favorites!' : 'Removed from favorites');
    } catch (error) {
      toast.error('Failed to update favorite. Please try again.');
    }
  };

  const handleShare = async () => {
    try {
      const recipeUrl = `${window.location.origin}/menu/${item._id}`;
      await navigator.clipboard.writeText(recipeUrl);
      toast.success('Recipe link copied to clipboard!');
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      toast.error('Unable to copy link. Please copy the URL from your browser.');
    }
  };

  const handlePlanClick = async () => {
    if (!user) {
      toast.error('Please sign in to use the meal plan feature');
      return;
    }
    
    // Check menu source compatibility before showing modal
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/mealplan/${user.sub}/check-menu-source/${item._id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (!data.canAdd) {
          // Show the source conflict message
          toast.error(data.message, { duration: 5000 });
          return;
        }
      }
    } catch (error) {
      console.error('Error checking menu source:', error);
      // Continue anyway if check fails
    }
    
    setShowMealPlanModal(true);
  };

  const addCurrentMenuItemToShoppingList = async () => {
    const selectedIndices = item.ingredients
      .map((ing, index) => ({ ing, index }))
      .filter(({ ing }) => !ing.isSpice && !ing.isOil)
      .map(({ index }) => index);

    if (user) {
      await cartService.addMenuToCart(
        item._id,
        selectedIndices,
        parseFloat(preferredServings) || item.servings,
        includeSpices,
        includeOils
      );
    } else {
      await guestCartService.addMenuToCart(
        item._id,
        selectedIndices,
        parseFloat(preferredServings) || item.servings,
        includeSpices,
        includeOils,
        item
      );
    }

    try {
      await fetch(`${process.env.REACT_APP_API_URL}/menu/${item._id}/metrics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ incrementCartAdds: 1 })
      });
    } catch (metricsError) {
      console.error('Failed to update cart metrics:', metricsError);
    }

    gaEvent({ action: 'add_to_cart', category: 'Shopping', label: item._id });
  };

  const handleAddToMealPlan = async (date, mealType, options = {}) => {
    if (!user) {
      toast.error('Please sign in to add to meal plan');
      return { success: false };
    }

    try {
      // First, save the ingredient preferences
      const preferencesResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/preferences/${user.sub}/ingredients/${item._id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            selectedIngredients: Array.from(selectedIngredients),
            preferredServings: parseFloat(preferredServings)
          }),
        }
      );

      if (!preferencesResponse.ok) {
        throw new Error('Failed to save ingredient preferences');
      }

      // Then add to meal plan
      const response = await fetch(`${process.env.REACT_APP_API_URL}/mealplan/${user.sub}/meals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          menuItemId: item._id,
          date,
          mealType
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.error === 'source_mismatch') {
          toast.error(errorData.message, { duration: 5000 });
          return { success: false };
        }
        throw new Error('Failed to add to meal plan');
      }

      if (options.addToShoppingList) {
        await addCurrentMenuItemToShoppingList();
      }

      return {
        success: true,
        addedToShoppingList: Boolean(options.addToShoppingList)
      };
    } catch (error) {
      console.error('Error adding to meal plan:', error);
      toast.error('Failed to add to meal plan. Please try again.');
      return { success: false };
    }
  };

  // Add a function to save preferences whenever they change
  const savePreferences = async (newSet, newServings, newSpices, newOils) => {
    if (!user || !item) return;

    try {
      // Use the provided newSet or fall back to the current selectedIngredients state
      const ingredientsToSave = newSet || selectedIngredients;
      
      // Use the provided newServings or fall back to the current preferredServings state
      const servingsToSave = newServings !== undefined ? newServings : parseFloat(preferredServings);
      
      // Use the provided toggle states or fall back to the current states
      const spicesToSave = newSpices !== undefined ? newSpices : includeSpices;
      const oilsToSave = newOils !== undefined ? newOils : includeOils;
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/preferences/${user.sub}/ingredients/${item._id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            selectedIngredients: Array.from(ingredientsToSave),
            preferredServings: servingsToSave,
            includeSpices: spicesToSave,
            includeOils: oilsToSave
          }),
        }
      );

      if (!response.ok) {
        console.error('Failed to save preferences:', await response.text());
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  const handleBuyIngredients = async () => {
    try {
      setIsLoading(true);

      const token = localStorage.getItem('token');

      // Track general metrics for all users (logged in or not)
      try {
        await fetch(`${process.env.REACT_APP_API_URL}/menu/${item._id}/metrics`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            incrementViews: 1,
            incrementOrders: 1
          })
        });
      } catch (metricsError) {
        // Don't block the user if metrics fail
        console.error('Failed to update metrics:', metricsError);
      }

      gaEvent({ action: 'buy_ingredients', category: 'Instacart', label: item._id });

      // Track user-specific activity if logged in
      if (token) {
        try {
          await fetch(`${process.env.REACT_APP_API_URL}/menu/${item._id}/user-activity`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              incrementViews: 1,
              incrementOrders: 1
            })
          });
        } catch (activityError) {
          // Don't block the user if activity tracking fails
          console.error('Failed to track user activity:', activityError);
        }
      }

      // Calculate scaling factor
      const scaleFactor = parseFloat(preferredServings) / item.servings;

      // Filter and scale ingredients based on selection
      const selectedIngredientsArray = item.ingredients
        .filter((_, index) => selectedIngredients.has(index))
        .map(ingredient => scaleIngredient(ingredient, scaleFactor));
      
      // Initialize services
      const instacart = new InstacartService();
      
      // Create a deep link URL for this specific menu item
      const baseUrl = window.location.origin;
      const menuItemUrl = `${baseUrl}/menu/${item._id}`;
      
      const instacartLink = await instacart.createShoppingList(
        selectedIngredientsArray, 
        `${item.title} (${preferredServings} servings)`,
        menuItemUrl,
        item._id
      );

      // Open the Instacart link in a new tab
      window.open(instacartLink, '_blank', 'noopener,noreferrer');
      
      // Show success message
      toast.success('Selected ingredients added to your Instacart!');
    } catch (error) {
      console.error('Error adding to Instacart:', error);
      toast.error('Failed to add ingredients to cart');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToList = async () => {
    try {
      setAddingToList(true);
      await addCurrentMenuItemToShoppingList();

      setAddedToList(true);
      toast.success('Added to shopping list!');
      setTimeout(() => setAddedToList(false), 2000);
    } catch (error) {
      console.error('Error adding to list:', error);
      toast.error('Failed to add to list');
    } finally {
      setAddingToList(false);
    }
  };

  const nonSpiceOilCount = item?.ingredients
    ? item.ingredients.filter(ing => !ing.isSpice && !ing.isOil).length
    : 0;

  const handleEditSuccess = (updatedItem) => {
    // Close the edit form
    setShowEditForm(false);
    
    // Close the detail view and pass the updated item back
    if (onClose) {
      // Force a window reload to refresh the menu list
      window.location.reload();
      // Then close the detail view
      onClose(updatedItem);
    }
  };

  const recipeJsonLd = buildRecipeJsonLd(item);
  const recipeBreadcrumbs = item
    ? buildBreadcrumbs([
        { name: 'Home', url: '/' },
        { name: 'Recipes', url: '/menu' },
        { name: item.title || 'Recipe', url: `/menu/${item.menuId || item._id}` },
      ])
    : null;

  return (
    <div className="menu-detail-overlay" onClick={handleClose}>
      {item && recipeJsonLd && (
        <SEO
          title={`${item.title} Recipe — ingredients, instructions & nutrition`}
          description={
            item.description?.slice(0, 200) ||
            `Make ${item.title} at home. Full ingredients, step-by-step instructions, and nutrition info on FreshFitLab.`
          }
          canonical={`/menu/${item.menuId || item._id}`}
          image={item.image}
          type="article"
          jsonLd={[recipeJsonLd, recipeBreadcrumbs]}
        />
      )}
      <div className="menu-detail-wrapper">
        <div className="menu-detail-container" onClick={e => e.stopPropagation()}>
          <div className="menu-detail-actions no-print">
            <div className="menu-detail-buttons-group">
              <button 
                onClick={handleLike}
                className={`menu-detail-like-button ${hasLiked ? 'liked' : ''}`}
                title={!user ? 'Sign in to like recipes' : ''}
              >
                <i className="fas fa-heart"></i>
                <span>{likesCount}</span>
              </button>

              <button
                onClick={handlePlanClick}
                className="menu-detail-plan-button"
              >
                <i className="fas fa-calendar-plus"></i> Plan
              </button>

              <button 
                className="menu-detail-add-to-list-btn"
                onClick={handleAddToList}
                disabled={addingToList}
              >
                {addedToList ? (
                  <><i className="fas fa-check"></i> Added!</>
                ) : addingToList ? (
                  <><i className="fas fa-spinner fa-spin"></i> Adding...</>
                ) : (
                  <><i className="fas fa-cart-plus"></i> Add to list</>
                )}
              </button>

              <div className="midl-more-wrapper">
                <button 
                  className="midl-more-btn"
                  onClick={() => setShowMoreMenu(!showMoreMenu)}
                >
                  <i className="fas fa-ellipsis-v"></i> More
                </button>
                {showMoreMenu && (
                  <>
                    <div className="midl-more-backdrop" onClick={() => setShowMoreMenu(false)} />
                    <div className="midl-more-dropdown">
                      {user && (
                        <button onClick={() => { setShowMoreMenu(false); handleToggleFavorite(); }}>
                          <i className={`fa${isFavorited ? 's' : 'r'} fa-star`}
                             style={isFavorited ? { color: '#f6ad55' } : {}}
                          ></i> {isFavorited ? 'Remove from Favorites' : 'Add to Favorites'}
                        </button>
                      )}
                      {user && (
                        <button 
                          disabled={addingToCK}
                          onClick={async () => {
                            setShowMoreMenu(false);
                            if (ckActiveMatch) {
                              // Add directly to the active match
                              setAddingToCK(true);
                              try {
                                const match = await getMatch(ckActiveMatch);
                                const existing = (match.selectedMenuItems || []).map(m => ({
                                  menuItemId: m.menuItemId?._id || m.menuItemId,
                                  servings: m.servings || 1
                                }));
                                if (existing.find(e => e.menuItemId === item._id)) {
                                  toast('Already in this match\'s menu!');
                                } else {
                                  await selectMenuItems(ckActiveMatch, [...existing, { menuItemId: item._id, servings: item.servings || 4 }]);
                                  toast.success('Added to Community Kitchen match!');
                                }
                              } catch (err) {
                                toast.error(err.message);
                              } finally {
                                setAddingToCK(false);
                              }
                            } else {
                              // No active match — save to a CK wishlist in sessionStorage
                              const saved = JSON.parse(sessionStorage.getItem('ck_saved_menus') || '[]');
                              if (!saved.find(s => s._id === item._id)) {
                                saved.push({ _id: item._id, title: item.title, imageUrl: item.image, servings: item.servings || 4 });
                                sessionStorage.setItem('ck_saved_menus', JSON.stringify(saved));
                                toast.success('Saved for Community Kitchen! Open a match to add it.');
                              } else {
                                toast('Already saved for Community Kitchen');
                              }
                            }
                          }}
                        >
                          <i className="fas fa-hands-helping"></i> {ckActiveMatch ? 'Add to CK Match' : 'Save for Community Kitchen'}
                        </button>
                      )}
                      <button onClick={() => { setShowMoreMenu(false); handleBuyIngredients(); }}>
                        <img src="/images/Instacart_Carrot.png" alt="Instacart" className="midl-instacart-icon" />
                        Buy on Instacart
                      </button>
                      <button onClick={() => { setShowMoreMenu(false); handleShare(); }}>
                        <i className="fas fa-share-alt"></i> Share Recipe
                      </button>
                      <button onClick={() => { setShowMoreMenu(false); handlePrint(); }}>
                        <i className="fas fa-print"></i> Print
                      </button>
                      {isAdmin && (
                        <button onClick={() => { setShowMoreMenu(false); setShowEditForm(true); }}>
                          <i className="fas fa-edit"></i> Edit Recipe
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            <button className="menu-detail-close-button no-print" onClick={handleClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          {showMealPlanModal && (
            <MealPlanModal
              menuItem={item}
              onClose={() => setShowMealPlanModal(false)}
              onSave={handleAddToMealPlan}
            />
          )}

          {showEditForm && (
            <MenuEditForm
              onSuccess={handleEditSuccess}
              initialData={{
                ...item,
                _id: item._id // Ensure _id is explicitly passed
              }}
            />
          )}

          <div id="printable-recipe">
            <img src={item.image} alt={item.title} className="detail-image" />
            <div className="menu-detail-header">
              <div className="header-content">
                <h1>{item.title}</h1>
                <div className="time-info">
                  <div className="prep-time">
                    <i className="far fa-clock"></i>
                    <span>Prep: {cleanTimeDisplay(item.prepTime)}</span>
                  </div>
                  <div className="cook-time">
                    <i className="fas fa-fire"></i>
                    <span>Cook: {cleanTimeDisplay(item.cookTime)}</span>
                  </div>
                  <div className="total-time">
                    <i className="fas fa-hourglass-half"></i>
                    <span>Total: {cleanTimeDisplay(item.totalTime)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="menu-detail-content">
              <section className="description-section">
                <h3>Description</h3>
                <p>{item.detailedDescription || item.description}</p>
                {item.affiliateDetails?.slug && (
                  <div className="creator-page-link">
                    <a href={`/creator/${item.affiliateDetails.slug}`}>
                      <i className="fas fa-user-circle"></i> View all recipes by {item.affiliateDetails.businessName || 'this creator'}
                    </a>
                  </div>
                )}
                {item.affiliateDetails?.website && (
                  <div className="affiliate-website">
                    <p>
                      <i className="fas fa-globe"></i>{' '}
                      <a href={item.affiliateDetails.website} target="_blank" rel="noopener noreferrer">
                        Visit website
                      </a>
                    </p>
                  </div>
                )}
              </section>

              {/* Tags and Dietary Info Section */}
              {((item.dietary && item.dietary.length > 0) || publicTagsFrom(item.tags).length > 0) && (
                <section className="tags-dietary-section">
                  {item.dietary && item.dietary.length > 0 && (
                    <div className="dietary-info">
                      <div className="dietary-badges">
                        {item.dietary.map((diet, index) => (
                          <span key={index} className="dietary-badge">
                            <i className="fas fa-leaf"></i>
                            {diet}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {publicTagsFrom(item.tags).length > 0 && (
                    <div className="recipe-tags">
                      <div className="tags-list">
                        {publicTagsFrom(item.tags).map((tag, index) => (
                          <span key={index} className="recipe-tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              )}

              <button 
                className="add-to-list-primary-button"
                onClick={handleAddToList}
                disabled={addingToList}
              >
                {addedToList ? (
                  <><i className="fas fa-check"></i> Added to list!</>
                ) : addingToList ? (
                  <><i className="fas fa-spinner fa-spin"></i> Adding...</>
                ) : (
                  <><i className="fas fa-cart-plus"></i> Add to list ({nonSpiceOilCount} ingredients)</>
                )}
              </button>
              {item.ingredients && item.ingredients.length > 0 && (
                <section className="ingredients-section">
                  <div className="ingredients-header">
                    <h3>Ingredients</h3>
                    <div className="servings-control">
                      <label htmlFor="servings-input">Servings:</label>
                      <div className="servings-stepper">
                        <button 
                          type="button"
                          className="servings-button"
                          onClick={decrementServings}
                          disabled={parseFloat(preferredServings) <= 0.5}
                        >
                          <i className="fas fa-minus"></i>
                        </button>
                        <input
                          id="servings-input"
                          type="number"
                          min="0.5"
                          step="0.5"
                          value={preferredServings}
                          onChange={handleServingsChange}
                          onBlur={handleServingsBlur}
                          className="servings-input"
                        />
                        <button 
                          type="button"
                          className="servings-button"
                          onClick={incrementServings}
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                      <span className="servings-info">
                        (Original recipe: {item.servings} {item.servings === 1 ? 'serving' : 'servings'})
                      </span>
                    </div>
                  </div>
                  
                  <div className="ingredients-tip">
                    <i className="fas fa-info-circle"></i>
                    Uncheck any ingredients you don't need to buy
                  </div>
                  
                  <ul className="ingredients-list">
                    {item.ingredients.map((ingredient, index) => {
                      const scaledIngredient = scaleIngredient(
                        ingredient, 
                        parseFloat(preferredServings) / item.servings
                      );
                      return (
                        <li key={index} className="ingredient-item">
                          <label className="ingredient-label">
                            <input
                              type="checkbox"
                              checked={shouldSelectIngredient(ingredient, index)}
                              onChange={() => toggleIngredient(index)}
                              className="ingredient-checkbox"
                            />
                            <span className="ingredient-text">
                              {typeof scaledIngredient === 'object' ? (
                                <>
                                  {(() => {
                                    const parts = formatIngredientParts(scaledIngredient, 1, weightUnit);
                                    return (
                                      <>
                                        {parts.amount && (
                                          <span className="ingredient-quantity">{parts.amount}</span>
                                        )}
                                        {' '}
                                        {parts.unit && (
                                          <span className="ingredient-unit">{parts.unit}</span>
                                        )}
                                      </>
                                    );
                                  })()}
                                  {' '}
                                  <span className="ingredient-name">{scaledIngredient.name}</span>
                                  {scaledIngredient.prepNotes && (
                                    <span className="ingredient-prep-notes">
                                      {' '}
                                      {scaledIngredient.prepNotes}
                                    </span>
                                  )}
                                </>
                              ) : (
                                <span className="ingredient-name">{scaledIngredient}</span>
                              )}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>

                  <div className="ingredients-controls">
                    <div className="ingredient-toggles">
                      <div className="toggle-group">
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={includeSpices}
                            onChange={(e) => handleSpiceToggle(e.target.checked)}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                        <span className="toggle-label">Include all spices</span>
                      </div>
                      
                      <div className="toggle-group">
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={includeOils}
                            onChange={(e) => handleOilToggle(e.target.checked)}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                        <span className="toggle-label">Include all oils</span>
                      </div>
                    </div>
                    <button 
                      className="select-all-btn"
                      onClick={handleSelectAll}
                    >
                      Select All
                    </button>
                  </div>
                </section>
              )}

              {item.equipment && item.equipment.length > 0 && (
                <section className="equipment-section">
                  <h3>Kitchen Equipment Needed</h3>
                  <ul className="menu-item-equipment-list">
                    {item.equipment.map((tool, index) => (
                      <li key={index}>
                        <i className="fas fa-utensils" aria-hidden="true" />
                        <span>{tool}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {item.instructions && item.instructions.length > 0 && (
                <section className="menu-instructions-section">
                  <h3>Cooking Instructions</h3>
                  <ol className="menu-instructions-list">
                    {item.instructions.map((step, index) => (
                      <li key={index}>
                        <p>{step}</p>
                      </li>
                    ))}
                  </ol>
                </section>
              )}

              {item.nutrition && Object.keys(item.nutrition).length > 0 && (
                <section className="nutrition-section">
                  <h3>Nutrition Facts</h3>
                  <div className="servings-info">
                    Servings: {item.servings}
                  </div>
                  <div className="amount-per-serving">Amount per serving</div>
                  <div className="calories-row">
                    <span className="calories-label">Calories</span>
                    <span className="calories-value">{(String(item.nutrition.Calories).match(/\d+(?:\.\d+)?/) || [item.nutrition.Calories])[0]}</span>
                  </div>
                  <div className="nutrition-table">
                    {Object.entries(item.nutrition).map(([key, value]) => {
                      if (key === 'Calories') return null; // Skip calories as it's shown above
                      
                      // Daily Value percentages (example values - adjust as needed)
                      const dailyValues = {
                        'Protein': 100,
                        'Carbohydrates': 275,
                        'Fat': 75,
                        'Fiber': 28,
                        'Sodium': 2300 // in mg
                      };

                      let amount, unit, dailyValuePercent;

                      // Some extracted values contain verbose text (e.g.
                      // "6g protein." or "30g carbohydrate (20g sugars)"), so
                      // pull just the leading number + optional unit instead of
                      // rendering the whole string and suffixing "g".
                      const valueStr = value == null ? '' : String(value);
                      const defaultUnit = key === 'Sodium' ? 'mg' : 'g';
                      const match = valueStr.match(/(\d+(?:\.\d+)?)\s*(mg|g)?/i);
                      amount = match ? match[1] : valueStr;
                      unit = match && match[2] ? match[2].toLowerCase() : (match ? defaultUnit : '');
                      dailyValuePercent = Math.round((parseFloat(amount) / dailyValues[key]) * 100);

                      return (
                        <div key={key} className={`nutrition-row ${key.startsWith(' ') ? 'nutrition-subrow' : ''}`}>
                          <span className="nutrition-label">{key}</span>
                          <span className="nutrition-value">
                            {amount}{unit}
                          </span>
                          <span className="daily-value">
                            {!isNaN(dailyValuePercent) ? `${dailyValuePercent}%` : ''}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="nutrition-footer">
                    <p>* The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet.</p>
                    <p>2,000 calories a day is used for general nutrition advice.</p>
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuItemDetail; 